import { createSelector } from 'reselect';

// Selectors
import { breadcrumbSelector } from '../../workspace/breadcrumbs/breadcrumbSelector';
import { boardVisibleElementGraphSelector } from '../selectors/elementGraphSelector';
import {
    getIsAsyncResourceEntityFetched,
    getIsAsyncResourceEntityFetching,
} from '../../utils/services/http/asyncResource/asyncResourceSelector';

// Utils
import { getElements } from '../utils/elementStoreUtils';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getLabels, getTitle } from '../../../common/elements/utils/elementPropertyUtils';
import { getUniqueArray } from '../../../common/utils/arrayUtils';

// Types
import { Label, LabelMap, LabelsByBoards } from '../../../common/labels/labelTypes';
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';

export const selectLabels = (state: Immutable.Map<string, unknown>) => state.get('labels') as LabelMap | undefined;

export const selectLabelsByIds =
    (labelIds: Immutable.List<string>) =>
    (state: Immutable.Map<string, object>): Immutable.List<Label> | undefined => {
        if (!labelIds || labelIds.size === 0) return;

        const allLabels = selectLabels(state) || {};
        return labelIds.map((labelId) => labelId && allLabels[labelId]).filter(Boolean) as Immutable.List<Label>;
    };

export const selectBoardsToFetchForCurrentPath = createSelector(
    breadcrumbSelector,
    (state: Immutable.Map<string, unknown>) => state,
    (breadcrumbs, state): { toFetch: string[]; isBoardFetchInProgress: boolean } => {
        const toFetch: string[] = [];
        let isBoardFetchInProgress = false;

        breadcrumbs?.path?.forEach((boardId: string) => {
            if (getIsAsyncResourceEntityFetching(state, ResourceTypes.boards, boardId)) {
                if (!isBoardFetchInProgress) isBoardFetchInProgress = true;
                return;
            }

            if (!getIsAsyncResourceEntityFetched(state, ResourceTypes.boards, boardId)) toFetch.push(boardId);
        });

        return {
            toFetch,
            isBoardFetchInProgress,
        };
    },
);

export const selectCurrentPathLabels = createSelector(
    breadcrumbSelector,
    getElements,
    boardVisibleElementGraphSelector,
    selectLabels,
    (breadcrumbs, elements, visibleElementsGraph, labelMap = {}): LabelsByBoards => {
        const usedLabelIds = new Set<string>();
        return (
            breadcrumbs?.path?.reduce((acc: LabelsByBoards, boardId: string): LabelsByBoards => {
                const visibleElements = visibleElementsGraph?.[boardId] || [];
                const boardLabelIds: string[] = [];

                visibleElements.forEach((elementId) => {
                    const element = getElement(elements, elementId);
                    const labels = getLabels(element);
                    if (!labels || !labels.length) return;

                    boardLabelIds.push(...labels);
                });

                // Make sure we aren't including labels that have already been included from a parent board
                const uniqueBoardLabelIds = getUniqueArray(boardLabelIds).filter(
                    (labelId: string) => !usedLabelIds.has(labelId),
                );

                if (!uniqueBoardLabelIds.length) return acc;

                uniqueBoardLabelIds.forEach((labelId) => usedLabelIds.add(labelId));

                const board = getElement(elements, boardId);
                const boardLabels = uniqueBoardLabelIds.map((labelId) => labelMap[labelId]).filter(Boolean) as Label[];

                acc.push({
                    title: getTitle(board),
                    labels: boardLabels,
                });

                return acc;
            }, []) || []
        );
    },
);
